import React, { useState, useEffect, useCallback } from 'react';
import { Table, Typography, Card, TabPane, Tabs, Modal, Input, Button, Toast, Space, Tag } from '@douyinfe/semi-ui';
import { IconModalStroked, IconSearch } from '@douyinfe/semi-icons';
import { formatAmountZhCn, formatDate } from '../../utils';
import { ApiClient } from '../../api/ApiClient';
import OutMoneyPlan, { OutMoneyPlanProps } from './indexOutMoneyPlan';

const { Text } = Typography;
let apiClient = ApiClient.getDefault();

interface TreeNode {
    id?: number;
    groupId: number;
    groupName: string;
    interestAmount: number;
    realInterestAmount: number | null;
    interestRate?: number;
    postingInterestRate?: number;
    borrowInterestRate?: number;
    updatedBy?: string;
    updatedAt?: string;
    childList?: TreeNode[];
}

const isLeadTab = (tab: string) => tab === 'lead';

export default function LeadExtPage() {
    const [activeTab, setActiveTab] = useState('lead');
    const [data, setData] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState<(string | number)[]>([]);

    const [detailShow, setDetailShow] = useState(false);
    const [outMoneyPlanProps, setOutMoneyPlanProps] = useState<OutMoneyPlanProps | undefined>(undefined);

    const [filterLeadId, setFilterLeadId] = useState('');
    const [filterLeadName, setFilterLeadName] = useState('');
    const [filterOutMoneyName, setFilterOutMoneyName] = useState('');

    const loadData = async () => {
        setLoading(true);
        try {
            let url = activeTab === 'outMoney' ? '/mortgageLeadExt/listByOutMoney' : '/mortgageLeadExt/listByLead';
            let params: any = {};
            if (filterLeadId) params.leadId = filterLeadId;
            if (filterLeadName) params.leadName = filterLeadName;
            if (filterOutMoneyName) params.outMoneyName = filterOutMoneyName;
            
            let res: any = await apiClient.request(url, 'get', params);
            if (res.code === 'ok') {
                setData(res.data || []);
                setExpandedRowKeys([]);
            }
        } catch (e) {
            console.error(e);
            Toast.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { 
        loadData(); 
    }, []);

    useEffect(() => { 
        loadData(); 
    }, [activeTab]);
    

    const totalInterest = data.reduce((sum, item) => sum + (item.interestAmount || 0), 0);
    const totalRealInterest = data.reduce((sum, item) => sum + (item.realInterestAmount || 0), 0);

    const openOutMoneyPlan = (outMoneyUserId?: number, leadId?: number) => {
        let p: OutMoneyPlanProps = {};
        if (outMoneyUserId) p.outMoneyUserId = String(outMoneyUserId);
        if (leadId) p.leadId = leadId;
        setOutMoneyPlanProps(p);
        setDetailShow(true);
    };

    const handleUpdateRealInterest = (record: TreeNode) => {
        let inputValue = record.realInterestAmount != null ? record.realInterestAmount : 0;
        Modal.confirm({
            title: `更新实际利息`,
            content: (
                <div style={{ marginTop: 12 }}>
                    <div style={{ marginBottom: 8 }}>
                        &nbsp;&nbsp;&nbsp;出款人: <Tag size='large' color='blue'>{record.groupName}</Tag> &nbsp;&nbsp;&nbsp;&nbsp; 利息: <Tag size='large' color='green'>{formatAmountZhCn(record.interestAmount)}</Tag>
                    </div>
                    <Input
                    prefix="实际利息："
                        defaultValue={inputValue}
                        placeholder="请输入实际利息"
                        type="number"
                        onChange={(v) => { inputValue = Number(v); }}
                    />
                </div>
            ),
            okText: '确认更新',
            cancelText: '取消',
            onOk: async () => {
                if (!inputValue) { Toast.warning('请输入实际利息'); return; }
                try {
                    let res: any = await apiClient.request('/mortgageLeadExt/updateRealInterest', 'put', null, {
                        id: record.id, realInterestAmount: inputValue,
                    });
                    if (res.code === 'ok') { Toast.success('更新成功'); loadData(); }
                    else { Toast.error(res.msg || '更新失败'); }
                } catch (e) { Toast.error('更新失败'); }
            },
        });
    };

    const handleParentInterestClick = (record: TreeNode) => {
        if (isLeadTab(activeTab)) openOutMoneyPlan(undefined, record.groupId);
        else openOutMoneyPlan(record.groupId, undefined);
    };

    const handleChildInterestClick = (child: TreeNode, parent: TreeNode) => {
        if (isLeadTab(activeTab)) openOutMoneyPlan(child.groupId, parent.groupId);
        else openOutMoneyPlan(parent.groupId, child.groupId);
    };

    // 颜色主题
    const leadColor = '#1677ff';
    const outMoneyColor = '#722ed1';

    const primaryColor = isLeadTab(activeTab) ? leadColor : outMoneyColor;

    // 父表格列定义
    const parentColumns: any[] = [
        {
            title: <span style={{ color: primaryColor, fontWeight: 600 }}>{isLeadTab(activeTab) ? '客户' : '出款人'}</span>,
            dataIndex: 'groupName',
            width: 100,
            render: (v: string, record: TreeNode) => (
               
                    <span style={{ fontWeight: 600, color: primaryColor }}>{v}</span>
            ),
        },

        {
            title: '出款金额',
            dataIndex: 'outMoneyAmount',
            width: 80,
            render: (v: number,row:any) => <Text strong>{formatAmountZhCn(v-row.cardPostingOutMoneyAmount)}</Text>,
        },
        {
            title: '贷款代还',
            dataIndex: 'creditOutMoneyAmount',
            width: 80,
            render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
        },
        {
            title: '信用卡代还',
            dataIndex: 'cardOutMoneyAmount',
            width: 80,
            render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
        },
        {
            title: '信用卡代过账',
            dataIndex: 'cardPostingOutMoneyAmount',
            width: 90,
            render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
        },
        {
            title: '借款金额',
            dataIndex: 'borrowOutMoneyAmount',
            width: 80,
            render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
        },
        {
            title: '出款笔数',
            dataIndex: 'outMoneyCount',
            width: 80,
            render: (v: number) => <Tag size="small" color="cyan">{v ?? 0}</Tag>,
        },
        {
            title: '计息天数',
            dataIndex: 'interestDays',
            width: 80,
            render: (v: number) => v != null ? <Tag size="small" color="violet">{v}天</Tag> : '-',
        },
        {
            title: '利息',
            dataIndex: 'interestAmount',
            width: 80,
            render: (v: number, record: TreeNode) => (
                <Text strong link onClick={() => handleParentInterestClick(record)} style={{ color: '#e91e63' }}>
                    {formatAmountZhCn(v)}
                </Text>
            ),
        },
        {
            title: '实际利息',
            dataIndex: 'realInterestAmount',
            width: 100,
            render: (v: number | null) => (
                <Text strong style={{ color: '#00b578' }}>{v != null ? formatAmountZhCn(v) : '-'}</Text>
            ),
        },
        {
            title: isLeadTab(activeTab) ? '出款人数' : '客户数',
            width: 90,
            render: (_: any, record: TreeNode) => (
                <Tag color={isLeadTab(activeTab) ? 'purple' : 'blue'} size="small">
                    {record.childList?.length || 0}
                </Tag>
            ),
        },
    ];

    const renderExpandedRow = (parent: TreeNode) => {
        if (!parent.childList || parent.childList.length === 0) {
            return <Text type="tertiary">暂无数据</Text>;
        }
        const lead = isLeadTab(activeTab);
        // 子节点用另一种颜色
        const childColor = lead ? outMoneyColor : leadColor;

        const cols: any[] = [
            {
                title: <span style={{ color: childColor, fontWeight: 600 }}>{lead ? '出款人' : '客户'}</span>,
                dataIndex: 'groupName',
                width: 100,
                render: (v: string) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                            background: childColor,
                        }} />
                        <span style={{ fontWeight: 500, color: childColor }}>{v}</span>
                    </div>
                ),
            },
            
          
             {
                title: '出款金额',
                dataIndex: 'outMoneyAmount',
                width: 100,
                render: (v: number,row:any) => <Text strong>{formatAmountZhCn(v-row.cardPostingOutMoneyAmount)}</Text>,
            },
            {
                title: '贷款代还',
                dataIndex: 'creditOutMoneyAmount',
                width: 80,
                render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
            },
            {
                title: '信用卡代还',
                dataIndex: 'cardOutMoneyAmount',
                width: 80,
                render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
            },
            {
                title: '信用卡代过账',
                dataIndex: 'cardPostingOutMoneyAmount',
                width: 90,
                render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
            },
            {
                title: '借款金额',
                dataIndex: 'borrowOutMoneyAmount',
                width: 80,
                render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
            },
            {
                title: '出款笔数',
                dataIndex: 'outMoneyCount',
                width: 80,
                render: (v: number) => <Tag size="small" color="cyan">{v ?? 0}</Tag>,
            },
            {
                title: '计息天数',
                dataIndex: 'interestDays',
                width: 80,
                render: (v: number) => v != null ? <Tag size="small" color="violet">{v}天</Tag> : '-',
            },
            {
                title: '利率',
                dataIndex: 'interestRate',
                width: 80,
                render: (v: number) => v != null ? <Tag size="small">{v}%</Tag> : '-',
            },
            {
                title: '过账利率',
                dataIndex: 'postingInterestRate',
                width: 80,
                render: (v: number) => v != null ? <Tag size="small" color="blue">{v}%</Tag> : '-',
            },
            {
                title: '借款利率',
                dataIndex: 'borrowInterestRate',
                width: 80,
                render: (v: number) => v != null ? <Tag size="small" color="orange">{v}%</Tag> : '-',
            },
            {
                title: '利息',
                dataIndex: 'interestAmount',
                width: 80,
                render: (v: number, record: TreeNode) => (
                    <Text link onClick={() => handleChildInterestClick(record, parent)} style={{ color: '#e91e63' }}>
                        {formatAmountZhCn(v)}
                    </Text>
                ),
            },
            {
                title: '实际利息',
                dataIndex: 'realInterestAmount',
                width: 100,
                render: (v: number | null, record: TreeNode) => (
                    <Space>
                        <Text style={{ color: '#00b578' }}>{v != null ? formatAmountZhCn(v) : '-'}</Text>
                        {lead && (
                            <Button
                                size="small"
                                theme="borderless"
                                style={{ color: '#1677ff', padding: '0 4px', fontSize: 12 }}
                                onClick={() => handleUpdateRealInterest(record)}
                            >
                                编辑
                            </Button>
                        )}
                    </Space>
                ),
            },
            {
                title: '更新人',
                dataIndex: 'updatedBy',
                width: 80,
                render: (v: string) => v ? <Text style={{ color: '#666' }}>{v}</Text> : '-',
            },
            {
                title: '更新日期',
                dataIndex: 'updatedAt',
                width: 100,
                render: (v: string) => v ? <Text style={{ color: '#999', fontSize: 12 }}>{formatDate(v)}</Text> : '-',
            },
        ];
        return (
            <div style={{ paddingLeft: 28, borderLeft: `3px solid ${primaryColor}`, marginLeft: 12 }}>
                <Table
                    columns={cols}
                    dataSource={parent.childList}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    style={{ background: 'var(--semi-color-bg-1)', borderRadius: 4 }}
                />
            </div>
        );
    };

    return (
        <div>
            <Card style={{ marginBottom: 12, padding: '8px 16px'  }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <Tabs activeKey={activeTab} onChange={(key) => setActiveTab(key)} style={{ marginBottom: 0 }}>
                        <TabPane tab={<span style={{ color: leadColor }}>客户维度</span>} itemKey="lead" />
                        <TabPane tab={<span style={{ color: outMoneyColor }}>出款人维度</span>} itemKey="outMoney" />
                    </Tabs>
                    <div style={{ flex: 1 }} />
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>{isLeadTab(activeTab) ? '客户数' : '出款人数'}</span>
                        <span style={{ ...styles.statValue, color: primaryColor }}>{data.length}</span>
                    </div>
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>总利息</span>
                        <span style={{ ...styles.statValue, color: '#e91e63' }}>{formatAmountZhCn(totalInterest)}</span>
                    </div>
                    <div style={styles.statCard}>
                        <span style={styles.statLabel}>总实际利息</span>
                        <span style={{ ...styles.statValue, color: '#00b578' }}>{formatAmountZhCn(totalRealInterest)}</span>
                    </div>
                   

                     <Space style={{ marginRight: 16 }}>
                        <Input showClear prefix={<IconSearch />} placeholder="客户ID" value={filterLeadId} onChange={setFilterLeadId} style={{ width: 150 }} onEnterPress={loadData} />
                        <Input showClear prefix={<IconSearch />} placeholder="客户姓名" value={filterLeadName} onChange={setFilterLeadName} style={{ width: 150 }} onEnterPress={loadData} />
                        <Input showClear prefix={<IconSearch />} placeholder="出款人姓名" value={filterOutMoneyName} onChange={setFilterOutMoneyName} style={{ width: 150 }} onEnterPress={loadData} />
                        <Button icon={<IconSearch />} onClick={loadData}>查询</Button>
                    </Space>
                </div>
            </Card>

            <Card>
                <Table
                    columns={parentColumns}
                    dataSource={data}
                    rowKey="groupId"
                    loading={loading}
                    pagination={false}
                    scroll={{ y: 600 }}
                    expandedRowKeys={expandedRowKeys}
                    onExpand={(expanded: boolean, record: any) => {
                        setExpandedRowKeys(prev =>
                            expanded ? [...prev, record.groupId] : prev.filter(k => k !== record.groupId)
                        );
                    }}
                    expandedRowRender={renderExpandedRow}
                />
            </Card>

            <Modal
                icon={<IconModalStroked size="extra-large" style={{ color: "blue" }} />}
                title="出款明细"
                fullScreen={true}
                headerStyle={{ flexShrink: 0, borderBottom: "1px solid var(--semi-color-border)" }}
                bodyStyle={{ overflow: "auto", flex: 1 }}
                footer={null}
                hasCancel={false}
                onCancel={() => setDetailShow(false)}
                onOk={() => setDetailShow(false)}
                visible={detailShow}
            >
                <OutMoneyPlan
                    leadId={outMoneyPlanProps?.leadId}
                    outMoneyUserId={outMoneyPlanProps?.outMoneyUserId}
                />
            </Modal>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    statCard: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '6px 20px',
        borderRadius: 6,
        background: 'var(--semi-color-bg-2)',
        marginLeft: 8,
    },
    statLabel: {
        fontSize: 11,
        color: 'var(--semi-color-text-2)',
        marginBottom: 2,
    },
    statValue: {
        fontSize: 15,
        fontWeight: 'bold',
    },
};
